'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useState, useMemo, useCallback, useRef, startTransition, useOptimistic, useDeferredValue, Suspense, use } from 'react';
import { useConnection, useChainId } from 'wagmi';
import { Address } from 'viem';
import { formatUnits } from 'viem';
import Link from 'next/link';
import NextImage from 'next/image';
import { useRouter, useSearchParams } from 'next/navigation';
import { Button, Card, Skeleton, Tabs, Tab, Chip, CardBody, CardFooter, Image } from '@heroui/react';
import { useDirectListings } from '@/hooks/marketplace/useDirectListings';
import { useEnglishAuctions } from '@/hooks/marketplace/useEnglishAuctions';
import NFTCard from '@/components/marketplace/NFTCard';
import { chainSettings, type ChainType, ChainId, defaultNativeAddress } from '@/constants/chain-specific';
import { MARKETPLACE_CONTRACTS } from '@/constants/marketplace/contracts';
import type { NFTCollection } from '@/constants/marketplace/collections';
import toast from '@/util/toast';
import { SocialsButtons } from '@/components/Common/SocialsButtons';
import { getTokensByChainId } from '@/constants/marketplace/tokens';
import { config, defaultIpfsGateway, ipfsGateways } from '@/config/index';
import { readContract } from '@wagmi/core';
import { erc721Abi } from 'viem';
import { TokenConfigType } from '@/constants/types/currency';
import { getNFTMetadata } from '@/util/nftMetadata';
import { useNFTCollection } from '@/hooks/portfolio/useNFTCollection';
import { useOptimizedTokenLoading } from '@/hooks/useOptimizedTokenLoading';
import { useNFTViewer } from '@/hooks/useNFTViewer';
import { useSelectedNFTs } from '@/hooks/portfolio/useSelectedNFTs';
import { SellNFTDialog } from '@/components/marketplace';
import { motion, AnimatePresence } from "motion/react";
import { Icon as IconifyIcon } from '@iconify/react';
import { Pagination, ScrollShadow, useDisclosure } from '@heroui/react';
import { useTransition } from 'react';
import { cancelListingOnNFT } from '@/hooks/marketplace/useCancelListing';
import { findTokenByAddress } from '@/components/Portfolio/PortfolioDrawer';
import { isCollectionWhitelisted } from '@/constants/marketplace/collections';
import { type NFTToken } from '@/hooks/portfolio/useNFTCollectionOptimized';
import { getOptimizedImageUrl } from '@/util/ipfs';

// Define types for NFT metadata
interface NFTMetadata {
  name?: string;
  description?: string;
  image?: string;
  symbol?: string;
}

// Constants for wallet tab and listings pagination
const WALLET_ITEMS_PER_PAGE = 12; // Match the grid layout of listings
const LISTINGS_ITEMS_PER_PAGE = 12; // Items per page for listings tab

const fmt = new Intl.NumberFormat('en', {
  notation: 'compact',
  compactDisplay: 'short',
  minimumFractionDigits: 0,
  maximumFractionDigits: 1
});
interface SelectedNFT {
  id: string;
  contractAddress: Address;
  tokenId: string;
  collectionName: string;
  image?: string;
  name?: string;
}
interface WalletNFTCardProps {
  token: NFTToken;
  isSelected: boolean;
  collection?: NFTCollection;
  onToggleSelect: () => void;
  onViewNFT: () => void;
  chainId: ChainType;
  isListed?: boolean;
  isWhitelisted?: boolean;
  onSellRequest: (token: NFTToken) => void;
  tokenListing?: any;
  onCancelListing?: (listingId: string) => void;
  isTradingCard?: boolean;
}

// Define a minimal ERC1155 ABI for uri function
const erc1155AbiMinimal = [{
  inputs: [{
    name: 'id',
    type: 'uint256'
  }],
  name: 'uri',
  outputs: [{
    name: '',
    type: 'string'
  }],
  stateMutability: 'view',
  type: 'function'
}] as const;

// Grid skeleton component to maintain consistent layout
const GridSkeleton = t0 => {
  const $ = _c(2);
  const {
    count: t1
  } = t0;
  const count = t1 === undefined ? 12 : t1;
  let t2;
  if ($[0] !== count) {
    t2 = <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">{Array.from({
        length: count
      }).map(_temp)}</div>;
    $[0] = count;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
};

// Component that uses React 19's use() hook to prevent layout shift
const PageContent = ({
  pageDataPromise,
  currentPageItems,
  activeTab,
  chainId,
  nativeSymbol,
  contractAddress
}: {
  pageDataPromise: Promise<NFTCardData[]>;
  currentPageItems: any[];
  activeTab: string;
  chainId: ChainType;
  nativeSymbol: string;
  contractAddress: Address;
}) => {
  // This suspends until the data is ready, preventing layout shift
  const pageData = use(pageDataPromise);
  return <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
      {pageData.map(nft => {
      const item_0 = currentPageItems.find(item => item.tokenId.toString() === nft.tokenId && item.assetContract.toLowerCase() === nft.assetContractAddress.toLowerCase());
      if (item_0) {
        const supportedTokens = getTokensByChainId(chainId);
        const isNativeCurrency = item_0.currency.toLowerCase() === '0x0000000000000000000000000000000000000000' || item_0.currency.toLowerCase() === defaultNativeAddress.toLowerCase();
        const currencyInfo = isNativeCurrency ? {
          symbol: nativeSymbol,
          logo: chainSettings[chainId]?.nativeCurrency.logo || '/tokens/eth.png'
        } : supportedTokens?.tokens.find((token: {
          address: string;
        }) => token.address.toLowerCase() === item_0.currency.toLowerCase());
        const nftWithData = {
          ...nft,
          [activeTab === 'listings' ? 'listing' : 'auction']: {
            formattedPrice: formatUnits(item_0.pricePerToken || item_0.minimumBidAmount || 0n, 18),
            currencySymbol: currencyInfo?.symbol || nativeSymbol,
            currencyAddress: item_0.currency as Address,
            currencyLogo: currencyInfo?.logo,
            endsAt: item_0.endTimestamp ? new Date(Number(item_0.endTimestamp) * 1000).toLocaleDateString() : undefined
          }
        };
        return <NFTCard key={`${nft.id}-${nft.tokenId}`} nft={nftWithData} />;
      }
      return null;
    })}
    </div>;
};

// Wallet NFT Card component (matches the styling of listing cards)
const WalletNFTCard = React.memo<WalletNFTCardProps>(({
  token,
  isSelected,
  collection,
  onToggleSelect,
  onViewNFT,
  chainId,
  isListed,
  isWhitelisted,
  onSellRequest,
  tokenListing,
  onCancelListing,
  isTradingCard = false
}) => {
  const [processing, setProcessing] = useState(false);
  const [isPending, startTransition] = useTransition();

  // Process IPFS URL for display
  const {
    primaryUrl: primaryUrl_0,
    fallbackUrl: fallbackUrl_0
  } = useMemo(() => {
    if (!token.metadata?.image) {
      return {
        primaryUrl: null,
        fallbackUrl: null
      };
    }
    const imageUrl = token.metadata.image.trim();

    // If it's already a standard HTTP URL, use it directly
    if (imageUrl.startsWith('http') && !imageUrl.includes('/ipfs/')) {
      return {
        primaryUrl: imageUrl,
        fallbackUrl: null
      };
    }

    // Handle IPFS URLs
    let ipfsPath = '';
    if (imageUrl.startsWith('ipfs://')) {
      ipfsPath = imageUrl.replace('ipfs://', '');
    } else if (imageUrl.includes('/ipfs/')) {
      const ipfsIndex = imageUrl.indexOf('/ipfs/');
      ipfsPath = imageUrl.substring(ipfsIndex + 6);
    } else {
      return {
        primaryUrl: imageUrl,
        fallbackUrl: null
      };
    }
    const primaryUrl = `${defaultIpfsGateway}${ipfsPath}`;
    const fallbackUrl = `${ipfsGateways[1]}${ipfsPath}`;
    return {
      primaryUrl,
      fallbackUrl
    };
  }, [token.metadata?.image]);
  const handleCardClick = useCallback(() => {
    startTransition(() => {
      onToggleSelect();
    });
  }, [onToggleSelect]);
  const handleViewPress = useCallback(() => {
    startTransition(() => {
      onViewNFT();
    });
  }, [onViewNFT]);
  const handleSellPress = useCallback(() => {
    startTransition(() => {
      onSellRequest(token);
    });
  }, [onSellRequest, token]);
  const handleCancelListingPress = useCallback(async () => {
    if (!tokenListing || !onCancelListing) return;
    try {
      setProcessing(true);
      await onCancelListing(tokenListing.listingId);
      toast.success('Listing cancelled successfully!');
    } catch (error) {
      console.error('Error cancelling listing:', error);
      toast.error('Failed to cancel listing');
    } finally {
      setProcessing(false);
    }
  }, [tokenListing, onCancelListing]);
  const listingToken = findTokenByAddress(tokenListing?.currency, chainId);
  return <Card className="transition-all w-full hover:shadow-md border border-gray-200 dark:border-gray-800" isPressable>
      <div className="flex relative aspect-square w-full overflow-hidden justify-center items-center">
        <Image src={primaryUrl_0 || fallbackUrl_0 || '/placeholder.png'} alt={token.metadata?.name || `Token #${token.tokenId.toString()}`} className={`w-full transition-transform hover:scale-105 ${isTradingCard ? 'object-contain' : 'object-cover'}`} loading="lazy" isZoomed />
        {isListed && tokenListing && <div className="absolute bottom-1 right-2 text-xs text-secondary font-medium truncate z-30 bg-black/90 rounded-md py-1 px-2">
            Listed for {fmt.format(Number(tokenListing.pricePerToken) / 10 ** (listingToken?.decimals ?? 18))} {listingToken?.symbol}
          </div>}
      </div>
      <CardBody className="content-center">
        <div className="flex align-middle justify-center items-center">

          {/* Selection overlay */}
          {isSelected && <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
              <div className="bg-primary text-primary-foreground rounded-full p-2">
                <IconifyIcon icon="mdi:check" className="text-lg" />
              </div>
            </div>}


        </div>

        <div className="p-3 items-center">
          <div className="flex self-center text-md font-medium truncate ellipsis-2 mb-1">
            {token.metadata?.name || `Token #${token.tokenId.toString()}`}
          </div>
          <div className="text-md text-default-500 mb-2 pt-2">
            #{token.tokenId.toString()}
          </div>

        </div>
      </CardBody>
      <CardFooter className="flex justify-center mt-2 pt-2">
        {/* Action buttons overlay */}
        <div className="absolute bottom-2 left-2 right-2 flex gap-2 py-2">
          <Button as={Link} href={`/marketplace/${chainSettings[chainId]?.chainName}/${collection?.slug}/${token.tokenId}`} size="sm" variant="shadow" color="primary" className="flex-1 text-xs" startContent={<IconifyIcon icon="mdi:eye" className="text-sm" />}>
            View
          </Button>

          {isListed && tokenListing && onCancelListing ? <Button size="sm" variant="shadow" color="danger" className="flex-1 text-xs" startContent={<IconifyIcon icon="mdi:cancel" className="text-sm" />} onPress={handleCancelListingPress} isDisabled={processing}>
              {processing ? 'Cancelling...' : 'Cancel'}
            </Button> : isWhitelisted ? <Button size="sm" variant="shadow" color="primary" className="flex-1 text-xs" startContent={<IconifyIcon icon="mdi:tag" className="text-sm" />} onPress={handleSellPress}>
              Sell
            </Button> : null}
        </div>
      </CardFooter>
    </Card>;
});
WalletNFTCard.displayName = 'WalletNFTCard';

// Define the NFTCardData type to match the NFTCard component's expectations
type NFTCardData = {
  id: string;
  name: string;
  description: string;
  image: string;
  owner: string;
  tokenId: string;
  assetContractAddress: string;
  collectionName: string;
  chainId: number;
  symbol?: string;
  listing?: {
    formattedPrice: string;
    currencySymbol: string;
    currencyAddress?: Address;
    currencyLogo?: string;
    endsAt?: string;
  };
  auction?: {
    formattedMinimumBid: string;
    formattedHighestBid: string;
    currencySymbol: string;
    currencyAddress?: Address;
    currencyLogo?: string;
    endTimeInSeconds: number;
  };
  rarityRank?: number;
  maxSupply?: number;
};
interface CollectionPageClientProps {
  chainId: ChainType;
  contractAddress: Address;
  initialCollection: NFTCollection | null;
}

// Function to get marketplace contract address by chain id
const getMarketplaceContractAddress = (chainId: number) => {
  const marketplaceContract = MARKETPLACE_CONTRACTS.find(contract => contract.chain.id === chainId);
  return marketplaceContract?.address || '';
};
export default function CollectionPageClient({
  chainId,
  contractAddress,
  initialCollection
}: CollectionPageClientProps) {
  const {
    address,
    isConnected
  } = useConnection();
  const connectedChainId = useChainId();
  const isWrongNetwork = connectedChainId !== chainId;
  const marketplaceAddress = getMarketplaceContractAddress(chainId);
  const router = useRouter();
  const searchParams = useSearchParams();

  // Track if we're currently updating URL to prevent sync conflicts
  const isUpdatingUrlRef = useRef(false);

  // Get initial values from URL parameters
  const initialTab = searchParams.get('tab') || 'listings';
  const initialListingsPage = parseInt(searchParams.get('listingsPage') || '1', 10);
  const initialWalletPage = parseInt(searchParams.get('walletPage') || '1', 10);

  // ALL STATE HOOKS MUST BE AT THE TOP - NEVER CONDITIONAL
  const [activeTab, setActiveTab] = useState(initialTab);
  const [nftData, setNftData] = useState<Map<number, NFTCardData[]>>(new Map());
  const [pagePromises, setPagePromises] = useState<Map<string, Promise<NFTCardData[]>>>(new Map());
  const [isLoadingNFTs, setIsLoadingNFTs] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  // Use React 19's useOptimistic for smooth page transitions
  const [listingsCurrentPage, setListingsCurrentPage] = useState(initialListingsPage);
  const [optimisticListingsPage, setOptimisticListingsPage] = useOptimistic(listingsCurrentPage);
  const [listingsPreloadedPages, setListingsPreloadedPages] = useState<Set<number>>(new Set());

  // Wallet tab state
  const [walletCurrentPage, setWalletCurrentPage] = useState(initialWalletPage);
  const [optimisticWalletPage, setOptimisticWalletPage] = useOptimistic(walletCurrentPage);
  const [walletProcessing, setWalletProcessing] = useState(false);

  // Function to update URL parameters (simplified)
  const updateUrlParams = useCallback((updates: {
    tab?: string;
    listingsPage?: number;
    walletPage?: number;
  }) => {
    const params = new URLSearchParams(searchParams.toString());
    if (updates.tab !== undefined) {
      if (updates.tab === 'listings') {
        params.delete('tab'); // Remove default tab from URL
      } else {
        params.set('tab', updates.tab);
      }
    }
    if (updates.listingsPage !== undefined) {
      if (updates.listingsPage === 1) {
        params.delete('listingsPage'); // Remove default page from URL
      } else {
        params.set('listingsPage', updates.listingsPage.toString());
      }
    }
    if (updates.walletPage !== undefined) {
      if (updates.walletPage === 1) {
        params.delete('walletPage'); // Remove default page from URL
      } else {
        params.set('walletPage', updates.walletPage.toString());
      }
    }
    const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
    router.push(newUrl, {
      scroll: false
    });
  }, [searchParams, router]);

  // Fetch direct listings and auctions for this collection
  const {
    listings,
    loading: isLoadingListings
  } = useDirectListings({
    chainId,
    contractAddress: marketplaceAddress as Address,
    collectionAddress: contractAddress
  });
  const {
    auctions,
    loading: isLoadingAuctions
  } = useEnglishAuctions({
    chainId,
    contractAddress: marketplaceAddress as Address,
    collectionAddress: contractAddress
  });

  // Get the native token symbol for the current chain
  const nativeSymbol = chainSettings[chainId]?.nativeCurrency.symbol || 'ETH';

  // Use optimistic pages for immediate UI updates
  const displayListingsPage = optimisticListingsPage;
  const displayWalletPage = optimisticWalletPage;

  // Listings pagination calculations
  const listingsTotalPages = Math.ceil(listings.length / LISTINGS_ITEMS_PER_PAGE);
  const listingsStartIndex = (displayListingsPage - 1) * LISTINGS_ITEMS_PER_PAGE;
  const listingsEndIndex = listingsStartIndex + LISTINGS_ITEMS_PER_PAGE;
  const currentPageListings = listings.slice(listingsStartIndex, listingsEndIndex);

  // Auctions pagination calculations
  const auctionsTotalPages = Math.ceil(auctions.length / LISTINGS_ITEMS_PER_PAGE);
  const auctionsStartIndex = (displayListingsPage - 1) * LISTINGS_ITEMS_PER_PAGE;
  const auctionsEndIndex = auctionsStartIndex + LISTINGS_ITEMS_PER_PAGE;
  const currentPageAuctions = auctions.slice(auctionsStartIndex, auctionsEndIndex);

  // Wallet tab functionality - only load when wallet tab is active and user is connected
  const shouldLoadWallet = activeTab === 'wallet' && isConnected && !!address && !isWrongNetwork;
  const {
    tokens: walletTokens,
    balance: walletBalance,
    isLoading: isLoadingWalletTokens,
    refetch: refreshWalletTokens
  } = useNFTCollection(contractAddress, initialCollection?.name || 'Unknown Collection', chainId, address);

  // Optimized token loading for wallet tab
  const {
    currentPageTokens: walletCurrentPageTokens,
    isLoadingMetadata: isLoadingWalletMetadata
  } = useOptimizedTokenLoading({
    tokens: shouldLoadWallet ? walletTokens : [],
    currentPage: displayWalletPage,
    itemsPerPage: WALLET_ITEMS_PER_PAGE,
    contractAddress,
    chainId,
    isERC1155: initialCollection?.type === 'ERC1155'
  });

  // Selection management for wallet tab
  const {
    selectedNFTs: walletSelectedNFTs,
    toggleNFT: walletToggleNFT,
    isSelected: walletIsSelected,
    clearSelection: walletClearSelection
  } = useSelectedNFTs();

  // NFT viewer for wallet tab
  const {
    openNFTModal
  } = useNFTViewer();

  // Sell dialog for wallet tab
  const {
    isOpen: isWalletSellOpen,
    onOpen: onWalletSellOpen,
    onClose: onWalletSellClose
  } = useDisclosure();
  const [selectedWalletNFTForSell, setSelectedWalletNFTForSell] = useState<NFTToken | null>(null);

  // Check if collection is whitelisted for selling
  const isWhitelisted = useMemo(() => {
    return isCollectionWhitelisted(contractAddress, chainId);
  }, [contractAddress, chainId]);

  // Wallet pagination calculations
  const walletTotalPages = Math.ceil(walletBalance / WALLET_ITEMS_PER_PAGE);

  // Handle tab change
  const handleTabChange = useCallback((tab: string) => {
    setActiveTab(tab);
    updateUrlParams({
      tab
    });
  }, [updateUrlParams]);

  // Handle listings page change with React 19 optimistic updates
  const handleListingsPageChange = useCallback((page: number) => {
    // Immediately update optimistic state (prevents flash)
    setOptimisticListingsPage(page);

    // Update actual state and URL in a transition
    startTransition(() => {
      setListingsCurrentPage(page);
      updateUrlParams({
        listingsPage: page
      });
    });
  }, [updateUrlParams, setOptimisticListingsPage]);

  // Handle wallet page change with React 19 optimistic updates
  const handleWalletPageChange = useCallback((page_0: number) => {
    // Immediately update optimistic state (prevents flash)
    setOptimisticWalletPage(page_0);

    // Update actual state and URL in a transition
    startTransition(() => {
      setWalletCurrentPage(page_0);
      updateUrlParams({
        walletPage: page_0
      });
    });
  }, [updateUrlParams, setOptimisticWalletPage]);

  // Handle wallet cancel listing
  const handleWalletCancelListing = useCallback(async (listingId: string) => {
    try {
      setWalletProcessing(true);
      await cancelListingOnNFT({
        listingId: BigInt(listingId),
        chainId
      });
      toast.success('Listing cancelled successfully!');
      // Refresh both wallet tokens and listings
      refreshWalletTokens();
    } catch (error) {
      console.error('Error cancelling listing:', error);
      toast.error('Failed to cancel listing');
    } finally {
      setWalletProcessing(false);
    }
  }, [chainId, refreshWalletTokens]);

  // Function to truncate text with ellipsis
  const truncateDescription = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
  };

  // Function to fetch NFT data for a specific page
  const fetchPageData = useCallback(async (page_1: number, items: any[], tabType: string) => {
    if (items.length === 0) return [];
    try {
      const supportedTokens = getTokensByChainId(chainId);
      const nftPromises = items.map(async item => {
        // Get token currency info
        const currencyInfo = supportedTokens?.tokens.find(token => token.address.toLowerCase() === item.currency.toLowerCase());
        try {
          // Extract correct properties based on item type
          const assetContract = tabType === 'listings' ? (item as any).assetContract : (item as any).assetContractAddress;
          const tokenId = item.tokenId;

          // Get collection name and symbol
          let name, symbol;
          const collection = initialCollection;
          const isERC1155 = collection?.type === 'ERC1155';
          if (collection) {
            // Use collection data if available
            name = collection.name;
            symbol = collection.symbol;
          } else {
            // Try to fetch from contract (only works for ERC721)
            try {
              name = await readContract(config, {
                address: assetContract as Address,
                abi: erc721Abi,
                functionName: 'name',
                args: []
              });
              symbol = await readContract(config, {
                address: assetContract as Address,
                abi: erc721Abi,
                functionName: 'symbol',
                args: []
              });
            } catch (error_0) {
              // If it fails, use fallback values
              name = 'Unknown Collection';
              symbol = 'NFT';
            }
          }

          // Use proper metadata fetching system instead of manual contract calls
          let metadata: NFTMetadata = {};
          if (assetContract) {
            try {
              // Use the proper getNFTMetadata utility with collection-based token type detection
              metadata = await getNFTMetadata(assetContract as Address, tokenId, chainId, {
                useCache: true,
                skipCollectionConfig: false,
                isERC1155
              });
            } catch (error_1) {
              console.error('Error fetching NFT metadata:', error_1);
              metadata = {
                name: `${name} #${tokenId}`,
                description: 'Failed to load metadata',
                image: '/placeholder.png'
              };
            }
          } else {
            metadata = {
              name: `${name} #${tokenId}`,
              description: 'No contract address',
              image: '/placeholder.png'
            };
          }

          // Format price based on token decimals
          const decimals = currencyInfo?.decimals || 18;
          const price = tabType === 'listings' ? (item as any).pricePerToken : (item as any).buyoutCurrencyValue?.value || (item as any).minimumBidCurrencyValue?.value;
          const formattedPrice = formatUnits(price || 0n, decimals);
          const currencySymbol = currencyInfo?.symbol || nativeSymbol;

          // Get seller or creator address
          const ownerAddress = tabType === 'listings' ? (item as any).sellerAddress : (item as any).creatorAddress;

          // Get end time if available
          const endTime = tabType === 'listings' ? (item as any).endTimeInSeconds : (item as any).endTimeInEpochSeconds;

          // Get unique ID
          const itemId = tabType === 'listings' ? (item as any).id || `listing-${(item as any).listingId}-${tokenId}` : (item as any).id || `auction-${tokenId}`;

          // Create NFT card data with all required properties
          return {
            id: itemId,
            tokenId: tokenId.toString(),
            name: metadata.name || `${name} #${tokenId}`,
            description: metadata.description || '',
            image: getOptimizedImageUrl(metadata.image || '', 256, 256) || '',
            assetContractAddress: assetContract,
            owner: ownerAddress,
            collectionName: name as string,
            symbol: metadata.symbol || symbol as string || (name as string)?.split(' ')[0],
            chainId,
            [tabType === 'listings' ? 'listing' : 'auction']: {
              formattedPrice,
              currencySymbol,
              endsAt: endTime ? new Date(Number(endTime) * 1000).toISOString() : undefined
            }
          };
        } catch (err_0) {
          console.error('Error fetching token data:', err_0);
          return null;
        }
      });
      const results = await Promise.all(nftPromises);
      return results.filter(Boolean) as NFTCardData[];
    } catch (err) {
      console.error('Error fetching NFT data:', err);
      return [];
    }
  }, [chainId, nativeSymbol, initialCollection]);

  // Function to get or create a page data promise (prevents layout shift)
  const getPageDataPromise = useCallback((page_2: number, items_0: any[], tabType_0: string): Promise<NFTCardData[]> => {
    const cacheKey = `${tabType_0}-${page_2}`;

    // Return existing promise if available
    if (pagePromises.has(cacheKey)) {
      return pagePromises.get(cacheKey)!;
    }

    // Return cached data as resolved promise if available
    if (nftData.has(page_2)) {
      const resolvedPromise = Promise.resolve(nftData.get(page_2)!);
      setPagePromises(prev => new Map(prev).set(cacheKey, resolvedPromise));
      return resolvedPromise;
    }

    // Create new promise for fetching data
    const dataPromise = fetchPageData(page_2, items_0, tabType_0);
    setPagePromises(prev_0 => new Map(prev_0).set(cacheKey, dataPromise));

    // Cache the result when promise resolves
    dataPromise.then(result => {
      setNftData(prev_1 => new Map(prev_1).set(page_2, result));
    });
    return dataPromise;
  }, [pagePromises, nftData, fetchPageData]);

  // Load current page data for listings/auctions
  useEffect(() => {
    const loadCurrentPage = async () => {
      if (activeTab === 'wallet') return;
      const pageKey = `${activeTab}-${listingsCurrentPage}`;
      if (listingsPreloadedPages.has(listingsCurrentPage)) return;
      const items_1 = activeTab === 'listings' ? currentPageListings : currentPageAuctions;
      if (items_1.length === 0) return;
      setIsLoadingNFTs(true);
      try {
        const pageData = await fetchPageData(listingsCurrentPage, items_1, activeTab);
        setNftData(prev => new Map(prev).set(listingsCurrentPage, pageData));
        setListingsPreloadedPages(prev => new Set(prev).add(listingsCurrentPage));
      } finally {
        setIsLoadingNFTs(false);
      }
    };
    loadCurrentPage();
  }, [activeTab, listingsCurrentPage, currentPageListings, currentPageAuctions, fetchPageData, listingsPreloadedPages]);

  // Preload next page for listings/auctions
  useEffect(() => {
    const preloadNextPage = async () => {
      if (activeTab === 'wallet') return;
      const nextPage = listingsCurrentPage + 1;
      const totalPages = activeTab === 'listings' ? listingsTotalPages : auctionsTotalPages;
      if (nextPage > totalPages || listingsPreloadedPages.has(nextPage)) return;
      const nextStartIndex = (nextPage - 1) * LISTINGS_ITEMS_PER_PAGE;
      const nextEndIndex = nextStartIndex + LISTINGS_ITEMS_PER_PAGE;
      const allItems = activeTab === 'listings' ? listings : auctions;
      const nextPageItems = allItems.slice(nextStartIndex, nextEndIndex);
      if (nextPageItems.length === 0) return;
      try {
        const pageData = await fetchPageData(nextPage, nextPageItems, activeTab);
        setNftData(prev_2 => new Map(prev_2).set(nextPage, pageData));
        setListingsPreloadedPages(prev_3 => new Set(prev_3).add(nextPage));
      } catch (err_1) {
        console.error('Error preloading next page:', err_1);
      }
    };

    // Preload after a short delay to prioritize current page
    const timeoutId = setTimeout(preloadNextPage, 500);
    return () => clearTimeout(timeoutId);
  }, [activeTab, listingsCurrentPage, listingsTotalPages, auctionsTotalPages, listings, auctions, fetchPageData, listingsPreloadedPages]);

  // Sync state with URL parameters when they change (browser back/forward only)
  useEffect(() => {
    const urlTab = searchParams.get('tab') || 'listings';
    const urlListingsPage = parseInt(searchParams.get('listingsPage') || '1', 10);
    const urlWalletPage = parseInt(searchParams.get('walletPage') || '1', 10);

    // Update states to match URL (handles browser back/forward)
    if (urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
    if (urlListingsPage !== listingsCurrentPage) {
      setListingsCurrentPage(urlListingsPage);
      setOptimisticListingsPage(urlListingsPage);
    }
    if (urlWalletPage !== walletCurrentPage) {
      setWalletCurrentPage(urlWalletPage);
      setOptimisticWalletPage(urlWalletPage);
    }
  }, [searchParams, activeTab, listingsCurrentPage, walletCurrentPage, setOptimisticListingsPage, setOptimisticWalletPage]);

  // Get current page data for rendering
  const currentPageNFTData = nftData.get(listingsCurrentPage) || [];
  const fmt = new Intl.NumberFormat('en', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1
  });
  return <>
    {/* Collection banner/header */}
    {initialCollection?.banner && <div className="absolute top-0 left-0 w-full h-96 mb-6 overflow-hidden z-[1]">
        <NextImage src={initialCollection.banner} alt={`${initialCollection.name} banner`} fill style={{
        opacity: 0.3
      }} className="object-cover" onError={e => {
        const target = e.target as HTMLImageElement;
        target.src = '/placeholder.png';
      }} />
        {/* Custom gradient overlay with better fade control */}
        <div className="absolute inset-0 pointer-events-none" style={{
        background: 'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.1) 50%, rgba(42, 51, 68,0.7) 85%, rgba(42, 51, 68,1) 100%)'
      }}></div>
      </div>}
    <div className="container mx-auto pb-2 px-4 relative z-[2]">
      <div className="mb-6">
        <Link href="/marketplace" className="hover:underline flex items-center mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
            <path d="m15 18-6-6 6-6" />
          </svg>
          Back to Marketplace
        </Link>

        {initialCollection ? <>
            <div className="flex flex-wrap items-center gap-4 mb-2">
              {initialCollection.thumbnail && <div className="md:flex hidden relative w-16 h-16 rounded-full overflow-hidden mr-2">
                  <Image src={initialCollection.thumbnail} alt={`${initialCollection.name} thumbnail`} className="w-full object-cover" />
                </div>}

              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-2xl md:text-3xl font-bold">{initialCollection.name}</h1>
                </div>

                <div className="text-neutral-400 mt-2 space-y-1">
                  {initialCollection.maxSupply && initialCollection.type !== 'ERC1155' && <p>Max Supply: {fmt.format(initialCollection.maxSupply)} items</p>}
                </div>
              </div>
            </div>

            {/* Collection description with ellipsis */}
            {initialCollection.description && <div className="mt-4 mb-6 md:flex hidden">
                <p className="text-neutral-300">
                  {isDescriptionExpanded ? initialCollection.description : truncateDescription(initialCollection.description, 180)}
                </p>
                {initialCollection.description.length > 180 && <button onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)} className="mt-2 text-sm text-primary hover:underline">
                    {isDescriptionExpanded ? 'Show less' : 'Show more'}
                  </button>}
              </div>}

            {/* Collection links/socials */}
            {initialCollection.website && <div className="flex flex-wrap gap-3 mt-4 mb-6 align-middle items-center overflow-visible">
                <SocialsButtons socials={{
              website: initialCollection?.website,
              all: initialCollection?.socials
            }} className="flex-wrap" />
                {initialCollection?.mintLink && <Button as={Link} href={initialCollection?.mintLink} size="sm" color="primary" radius="full" className="text-sm">
                    Mint
                  </Button>}
              </div>}
          </> : <div className="space-y-4">
            <h1 className="text-4xl font-bold">Collection Not Found</h1>
            <p className="text-neutral-400">Unable to load collection metadata</p>
          </div>}
      </div>

      {isWrongNetwork ? <Card className="p-8 text-center mb-6">
          <h2 className="text-2xl font-semibold mb-4">Wrong Network</h2>
          <p className="mb-6">
            Please switch to {chainSettings[chainId]?.chainName || 'the correct network'} to view this collection.
          </p>
          <Button onPress={() => {
          // Network switching logic could be implemented here
          toast.info('Please switch your network manually in your wallet');
        }}>
            Switch Network
          </Button>
        </Card> : <div className="w-full">
          {/* HeroUI Tabs */}
          <Tabs selectedKey={activeTab} onSelectionChange={key => handleTabChange(key as string)} className="w-full" variant="underlined" color="secondary">
            <Tab key="listings" title={<div className="flex items-center space-x-2">
                  <span>Listings
                    {!isLoadingListings && listings?.length > 0 && <>{" " + "(" + listings?.length + ")"}</>}
                  </span>
                </div>}>
              {isLoadingListings ? <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  {[...Array(12)].map((_, i) => <Card key={i} className="overflow-hidden">
                      <Skeleton className="h-60 w-full" />
                      <div className="p-4">
                        <Skeleton className="h-6 w-3/4 mb-2" />
                        <Skeleton className="h-4 w-1/2" />
                      </div>
                    </Card>)}
                </div> : currentPageListings.length > 0 && activeTab === 'listings' ? <div className="space-y-6">
                  <Suspense fallback={<GridSkeleton count={LISTINGS_ITEMS_PER_PAGE} />}>
                    <PageContent pageDataPromise={getPageDataPromise(displayListingsPage, currentPageListings, activeTab)} currentPageItems={currentPageListings} activeTab={activeTab} chainId={chainId} nativeSymbol={nativeSymbol} contractAddress={contractAddress} />
                  </Suspense>

                  {/* Listings Pagination */}
                  {listingsTotalPages > 1 && <div className="flex justify-center">
                      <Pagination total={listingsTotalPages} page={displayListingsPage} onChange={handleListingsPageChange} showControls size="sm" classNames={{
                  wrapper: "gap-1 overflow-visible",
                  item: "w-8 h-8 text-small rounded-md bg-default-100/50 hover:bg-default-200",
                  cursor: "bg-primary text-primary-foreground font-bold shadow-md"
                }} />
                    </div>}

                  {/* Show current range and total for listings */}
                  <div className="flex justify-center">
                    <div className="text-xs text-default-500 bg-default-50 px-3 py-2 rounded-full">
                      Showing {listingsStartIndex + 1}-{Math.min(listingsEndIndex, listings.length)} of {listings.length} listings
                    </div>
                  </div>
                </div> : <div className="text-center py-12">
                  <h3 className="text-xl font-semibold mb-4">No Direct Listings Found</h3>
                  <p className="text-neutral-400">
                    There are currently no direct listings for this collection.
                  </p>
                </div>}
            </Tab>

            <Tab key="auctions" title={<div className="flex items-center space-x-2">
                  <span>Auctions
                    {!isLoadingAuctions && auctions?.length > 0 && <>{" " + "(" + auctions?.length + ")"}</>}</span>
                </div>}>
              {isLoadingAuctions ? <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  {[...Array(12)].map((__0, i_0) => <Card key={i_0} className="overflow-hidden">
                      <Skeleton className="h-60 w-full" />
                      <div className="p-4">
                        <Skeleton className="h-6 w-3/4 mb-2" />
                        <Skeleton className="h-4 w-1/2" />
                      </div>
                    </Card>)}
                </div> : currentPageAuctions.length > 0 && activeTab === 'auctions' ? <div className="space-y-6">
                  <Suspense fallback={<GridSkeleton count={LISTINGS_ITEMS_PER_PAGE} />}>
                    <PageContent pageDataPromise={getPageDataPromise(displayListingsPage, currentPageAuctions, activeTab)} currentPageItems={currentPageAuctions} activeTab={activeTab} chainId={chainId} nativeSymbol={nativeSymbol} contractAddress={contractAddress} />
                  </Suspense>

                  {/* Auctions Pagination */}
                  {auctionsTotalPages > 1 && <div className="flex justify-center">
                      <Pagination total={auctionsTotalPages} page={displayListingsPage} onChange={handleListingsPageChange} showControls size="sm" classNames={{
                  wrapper: "gap-1 overflow-visible",
                  item: "w-8 h-8 text-small rounded-md bg-default-100/50 hover:bg-default-200",
                  cursor: "bg-primary text-primary-foreground font-bold shadow-md"
                }} />
                    </div>}

                  {/* Show current range and total for auctions */}
                  <div className="flex justify-center">
                    <div className="text-xs text-default-500 bg-default-50 px-3 py-2 rounded-full">
                      Showing {auctionsStartIndex + 1}-{Math.min(auctionsEndIndex, auctions.length)} of {auctions.length} auctions
                    </div>
                  </div>
                </div> : <div className="text-center py-12">
                  <h3 className="text-xl font-semibold mb-4">No Live Auctions Found</h3>
                  <p className="text-neutral-400">
                    There are currently no live auctions for this collection.
                  </p>
                </div>}
            </Tab>
            <Tab key="wallet" title={<div className="flex items-center space-x-2">
                  <span>In Wallet
                    {isConnected && !isWrongNetwork && walletBalance > 0 && <>{" " + "(" + walletBalance + ")"}</>}
                  </span>
                </div>}>
              {!isConnected ? <div className="text-center py-12">
                  <IconifyIcon icon="mdi:wallet-outline" className="text-6xl text-default-400 mb-4" />
                  <h3 className="text-xl font-semibold mb-4">Connect Your Wallet</h3>
                  <p className="text-neutral-400">
                    Connect your wallet to view your NFTs from this collection.
                  </p>
                </div> : isWrongNetwork ? <div className="text-center py-12">
                  <IconifyIcon icon="mdi:network-off" className="text-6xl text-default-400 mb-4" />
                  <h3 className="text-xl font-semibold mb-4">Wrong Network</h3>
                  <p className="text-neutral-400 mb-6">
                    Please switch to {chainSettings[chainId]?.chainName || 'the correct network'} to view your NFTs.
                  </p>
                  <Button onPress={() => {
                toast.info('Please switch your network manually in your wallet');
              }}>
                    Switch Network
                  </Button>
                </div> : isLoadingWalletTokens ? <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  {[...Array(WALLET_ITEMS_PER_PAGE)].map((__1, i_1) => <Card key={i_1} className="overflow-hidden">
                      <Skeleton className="aspect-square w-full" />
                      <div className="p-3">
                        <Skeleton className="h-4 w-3/4 mb-2" />
                        <Skeleton className="h-3 w-1/2 mb-2" />
                        <Skeleton className="h-7 w-full" />
                      </div>
                    </Card>)}
                </div> : walletTokens.length === 0 ? <div className="text-center py-12">
                  <IconifyIcon icon="mdi:image-off" className="text-6xl text-default-400 mb-4" />
                  <h3 className="text-xl font-semibold mb-4">No NFTs Found</h3>
                  <p className="text-neutral-400">
                    You don't own any NFTs from this collection.
                  </p>
                </div> : <div className="space-y-6">
                  {/* NFT Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                    {walletCurrentPageTokens.map(token_0 => {
                  const tokenKey = `${token_0.contractAddress}-${token_0.tokenId.toString()}`;
                  const isTokenSelected = walletIsSelected(tokenKey);

                  // Find the specific listing for this token
                  const tokenListing = listings?.find(listing => listing.assetContract.toLowerCase() === contractAddress.toLowerCase() && listing.tokenId.toString() === token_0.tokenId.toString() && listing.status === 1 // CREATED status
                  );
                  const isTokenListed = !!tokenListing;
                  const isTradingCard = initialCollection?.isTradingCard || false;
                  return <AnimatePresence mode="wait" key={tokenKey}>
                          <motion.div key="portfolio" initial={{
                      opacity: 0,
                      x: 20
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} exit={{
                      opacity: 0,
                      x: -20
                    }} transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 30,
                      duration: 1
                    }} className="">
                            <WalletNFTCard token={token_0} collection={initialCollection || undefined} isSelected={isTokenSelected} onToggleSelect={() => {
                        const nftData_0 = {
                          id: tokenKey,
                          contractAddress: token_0.contractAddress,
                          tokenId: token_0.tokenId.toString(),
                          collectionName: token_0.collectionName,
                          image: token_0.metadata?.image,
                          name: token_0.metadata?.name
                        };
                        walletToggleNFT(nftData_0);
                      }} onViewNFT={() => {
                        const isERC1155_0 = initialCollection?.type === 'ERC1155';
                        openNFTModal({
                          chainId,
                          contractAddress: token_0.contractAddress,
                          tokenId: token_0.tokenId,
                          initialData: token_0.metadata ? {
                            metadata: token_0.metadata,
                            owner: address as Address,
                            isERC1155: isERC1155_0,
                            balance: undefined
                          } : undefined
                        });
                      }} chainId={chainId} isListed={isTokenListed} isWhitelisted={isWhitelisted} onSellRequest={token_1 => {
                        setSelectedWalletNFTForSell(token_1);
                        onWalletSellOpen();
                      }} tokenListing={tokenListing} onCancelListing={handleWalletCancelListing} isTradingCard={isTradingCard} />
                          </motion.div>
                        </AnimatePresence>;
                })}
                  </div>

                  {/* Pagination */}
                  {walletTotalPages > 1 && <div className="flex justify-center">
                      <Pagination total={walletTotalPages} page={displayWalletPage} onChange={handleWalletPageChange} showControls size="sm" classNames={{
                  wrapper: "gap-1 overflow-visible",
                  item: "w-8 h-8 text-small rounded-md bg-default-100/50 hover:bg-default-200",
                  cursor: "bg-primary text-primary-foreground font-bold shadow-md"
                }} />
                    </div>}

                  {/* Show total count for single page */}
                  {walletTotalPages === 1 && walletBalance > 4 && <div className="flex justify-center">
                      <div className="text-xs text-default-500 bg-default-50 px-3 py-2 rounded-full">
                        Showing all {walletBalance} NFTs
                      </div>
                    </div>}
                </div>}
            </Tab>
          </Tabs>
        </div>}

    </div>

    {/* Wallet Sell NFT Dialog */}
    {selectedWalletNFTForSell && <SellNFTDialog chainId={chainId} contractAddress={contractAddress} tokenId={selectedWalletNFTForSell.tokenId.toString()} collectionName={initialCollection?.name || 'Unknown Collection'} isOpen={isWalletSellOpen} onClose={() => {
      onWalletSellClose();
      setSelectedWalletNFTForSell(null);
      walletClearSelection();
    }} onSuccess={() => {
      walletClearSelection();
      refreshWalletTokens();
    }} />}
  </>;
}
function _temp(_, i) {
  return <Card key={i} className="overflow-hidden"><Skeleton className="aspect-square w-full" /><div className="p-3"><Skeleton className="h-4 w-3/4 mb-2" /><Skeleton className="h-3 w-1/2 mb-2" /><Skeleton className="h-7 w-full" /></div></Card>;
}